/* eslint-disable camelcase */
/* eslint-disable react/display-name */
/**
 * @file NutritionalIndicatorView.jsx
 * @author Daniel Aguirre
 * @description Nutritional Indicator View Page
 */

// Dependencies
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import DashboardCard from 'components/DashboardCard';
import DashboardTitle from 'components/Charts/DashboardTitle';
import ProgressBar from 'components/Charts/ProgressBar';
import DashboardFilter from 'components/Charts/DashboardFilter';
import { useHistory } from 'react-router';
import styleGuideColors from 'commons/styleGuideColors';
import EmptyChart from 'components/EmptyChart';
import useTruncateAndFormat from 'hook/useTruncateAndFormat';

/**
 * NutritionalIndicator View
 * @param { Object } dashboardStaffActions - DashboardStaff Actions.
 * @return { Node } React node.
 */

function NutritionalIndicatorView({ dashboardStaffActions, clinicId }) {
  const { t: translate } = useTranslation();
  const history = useHistory();
  const [chartData, setChartData] = useState(null);
  const [current, setCurrent] = useState(0);

  const { getNutritionalIndicator } = dashboardStaffActions;

  const stageMenu = {
    label: translate('indicators.stage'),
    items: [
      { key: 0, value: '4' },
      { key: 1, value: '5' }
    ]
  };

  const getCurrentStage = () => {
    return stageMenu.items[current].value;
  };

  const onGetData = (_, resp) => {
    const clasifications = [
      {
        name: 'Delgadez',
        color: styleGuideColors.baxterBrandCyan
      },
      {
        name: 'Normal',
        color: styleGuideColors.baxterBrandTeal
      },
      {
        name: 'Preobeso',
        color: styleGuideColors.graphYellow
      },
      {
        name: 'Obesidad',
        color: styleGuideColors.alertError
      }
    ];

    let data = [];
    let total = 0;

    if (resp?.data) {
      resp.data.forEach(({ cantidad }) => {
        total += cantidad;
      });

      clasifications.forEach(({ name, color }) => {
        const respElement = resp.data.find(
          ({ clasificacion }) => clasificacion === name
        );
        if (respElement) {
          const percent = Number((respElement.cantidad * 100) / total);
          data = [
            ...data,
            {
              title: translate(`dashboardStaff.${respElement.clasificacion}`),
              cantidad: respElement.cantidad,
              percent: useTruncateAndFormat(percent, 2),
              classification: respElement.clasificacion,
              color
            }
          ];
        }
      });
      setChartData(data);
    } else {
      setChartData([]);
    }
  };

  const getIndicatorInfo = (stage) => {
    setChartData([]);
    getNutritionalIndicator({ clinicId, stage }, onGetData);
  };

  useEffect(() => {
    getIndicatorInfo(getCurrentStage());
  }, [clinicId, translate]);

  const handleDetailClick = (index) => {
    const { classification, cantidad: totalItems } = chartData[index];
    history.push(
      `/main/dashboard-detail/nutritional-indicator/${getCurrentStage()}/${classification}/${totalItems}`
    );
  };

  return (
    <DashboardCard id="F27" loading={!chartData}>
      <div className="flex flex-wrap">
        <DashboardTitle
          title={translate('dashboardStaff.nutritionalIndicator')}>
          <DashboardFilter
            menuItems={stageMenu.items}
            label={stageMenu.label}
            current={getCurrentStage()}
            onSelectItem={(item) => {
              setCurrent(item.key);
              getIndicatorInfo(stageMenu.items[item.key].value);
            }}
          />
        </DashboardTitle>
        {chartData?.length === 0 && <EmptyChart />}
        <div className="px-2 w-full">
          {chartData?.map(({ color, title, subtitle, percent }, index) => (
            <div onClick={() => handleDetailClick(index)} key={index}>
              <ProgressBar
                color={color}
                title={title}
                subtitle={subtitle}
                percent={percent}
              />
            </div>
          ))}
        </div>
      </div>
    </DashboardCard>
  );
}

/**
 * Component definition of proptypes.
 */
NutritionalIndicatorView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getNutritionalIndicator: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default NutritionalIndicatorView;
